import config from 'config';
import Cookies from 'js-cookie';
import type { Options } from 'pusher-js';
import Pusher from 'pusher-js';
import * as domReader from 'utils/dom-reader';
import 'types/global';
import type {
  PusherChannelValue,
  PusherEventPayloadMap,
} from '@timelyapp/integration-cloud-api-spec';
import { getTicApiBaseUrl, getTicApiHeaders } from '@timelyapp/integration-cloud-sdk/api';
import { useCallbackProxy } from '@timelyapp/react';
import { useEffect } from 'react';
import type { PusherMessage } from 'types/pusher';
import type { ArgumentTypes } from 'types/utility';
import debug from './debug';
import { getLocalStorageObject } from './localStorage';

export const PUSHER_KEY_LOCALSTORAGE_KEY = 'pusherKey';

interface Headers {
  Authorization?: string;
  'X-CSRF-Token'?: string;
}

let pusherClient: Pusher | undefined;

interface GetPusherAppOptions {
  isProduction: boolean;
  baseUrl: string;
}
export const getPusherApp = (options: GetPusherAppOptions) => {
  if (!options.isProduction) {
    const localPusherKey = getLocalStorageObject<string>(PUSHER_KEY_LOCALSTORAGE_KEY);
    if (typeof localPusherKey === 'string' && localPusherKey.length > 0) {
      debug.template.pusher('Using custom Pusher credentials from localStorage', {
        localPusherKey,
        icon: '⚙️',
      });
      return { name: 'custom', key: localPusherKey };
    }
  }
  // Local dev: Rails (config/credentials) and TIC (apps/integration-cloud/.env) both
  // broadcast on the shared local app — pick its public client key so FE handshakes match.
  // …and, in a development build, a dev stack on its own hostnames (app.timelyapp-<stack>.localhost).
  const baseUrl = options.baseUrl ?? '';
  const isDevStackHost =
    process.env.NODE_ENV === 'development' && /\.localhost(:\d+)?(\/|$)/.test(baseUrl);
  if (baseUrl.includes('timelyapp.local') || isDevStackHost) {
    return { name: 'local', key: config.pusherAppKeys.local };
  }
  // Production
  if (options.baseUrl && options.baseUrl.includes('timelyapp.com')) {
    return { name: 'production', key: config.pusherAppKeys.production };
  }

  // Staging environments
  return { name: 'staging', key: config.pusherAppKeys.staging };
};

/**
 * A worktree dev stack's own Pusher-protocol server (Soketi), which a development build defines only when `dev` sets
 * it (scripts/inject-tic-vars-to-build-process.ts). Read as the literal `process.env.X` Vite replaces; without the
 * define, a browser has no `process` and the read throws, which means "not set". Production and staging builds never
 * use one.
 */
export const readDevStackPusher = () => {
  try {
    if (process.env.NODE_ENV !== 'development') return undefined;
    const host = process.env.DEV_PUSHER_HOST;
    if (!host) return undefined;
    return {
      host,
      port: Number(process.env.DEV_PUSHER_PORT),
      key: process.env.DEV_PUSHER_KEY ?? '',
    };
  } catch {
    return undefined;
  }
};

/** Client options for that server: plain websockets on its own host and port, no Pusher stats. */
export const devStackPusherOptions = (server: { host: string; port: number }): Options => ({
  wsHost: server.host,
  wsPort: server.port,
  forceTLS: false,
  enabledTransports: ['ws'],
  disableStats: true,
});

const useTic =
  !!process.env.FORCE_USE_TIC_PUSHER_CREDENTIALS_IN_TIMELY &&
  process.env.NODE_ENV === 'development';
/**
 * Set `FORCE_USE_TIC_PUSHER_CREDENTIALS_IN_TIMELY` in your .env file to use TIC Pusher instance in Timely FE.
 * PS: May throw 401's initially while TIC session is established.
 *
 * @warning In TIC mode, NO TIMELY PUSHER EVENTS WILL BE RECEIVED!
 * @link https://dashboard.pusher.com/apps/1631122 The Pusher app used in TIC development
 */
export function initializePusher() {
  // Timely defaults
  const app = getPusherApp(config);
  const isDevMode = !!window.BOOTSTRAPPED_DATA.standaloneClient;
  const apiUrl = window.BOOTSTRAPPED_DATA.host;
  const baseUrl = `${apiUrl}${apiUrl.includes('api') ? '/1.1' : ''}`;

  // In local dev, you can use Pusher with TIC's dev instance @ https://dashboard.pusher.com/apps/1631122
  if (useTic && !process.env.TIC_PUSHER_APP_KEY) throw new Error('TIC_PUSHER_APP_KEY is required');

  const devStack = readDevStackPusher();
  const pusherKey = devStack?.key || (useTic ? process.env.TIC_PUSHER_APP_KEY! : app.key);

  const headers: Headers = { 'X-CSRF-Token': domReader.getMetaTagName('csrf-token')! };
  let clientConfigOptions: Options = {};

  // Live, we just talk to Timely's session-based auth endpoint.
  if (!isDevMode) {
    clientConfigOptions = { authEndpoint: `${baseUrl}/pusher/auth`, auth: { headers } };
  }
  // IN DEVELOPMENT, we need different configs depending on the environment.
  else {
    // Timely's typical configuration, but with token-based auth
    if (!useTic) {
      headers.Authorization = `Bearer ${Cookies.get('token') || 'invalid'}`;
      clientConfigOptions = { authEndpoint: `${baseUrl}/pusher/auth`, auth: { headers } };
    }
    // TIC's configuration (NB)
    else {
      const ticBaseUrl = `${getTicApiBaseUrl()}/1.0`;
      clientConfigOptions = {
        userAuthentication: {
          endpoint: `${ticBaseUrl}/pusher/user-auth`,
          headers: getTicApiHeaders(),
          transport: 'ajax',
        },
        channelAuthorization: {
          endpoint: `${ticBaseUrl}/pusher/channel-auth`,
          headers: getTicApiHeaders(),
          transport: 'ajax',
        },
      };
    }
  }
  const origin = useTic ? 'TIC' : 'timely';
  const logDump = { clientConfigOptions, config, useTic, ...(devStack && { devStack }) } as const;
  debug.template.pusher('Initializing Pusher', { origin, ...logDump });

  const client = new Pusher(pusherKey, {
    cluster: config.pusherCluster,
    forceTLS: true,
    ...clientConfigOptions,
    ...(devStack && devStackPusherOptions(devStack)),
  });

  // Set global variable to hold pusher socket id
  // We'll read from this in api wrapper to avoid recieving pusher messages to things we initiate from "this" session.
  client.connection.bind('connected', () => {
    debug.template.pusher(`Connected to ${app.name}`, { origin, ...logDump, icon: '🟢' });
    window.TimelyPusherSocketId = client.connection.socket_id;
  });
  client.connection.bind('disconnected', () => {
    debug.template.pusher(`Disconnected`, { origin, ...logDump, icon: '🔴' });
  });

  // Pusher.logToConsole = true;
  client.connection.bind('error', (err: unknown) => {
    debug.template.pusher(`Error`, { origin, ...logDump, icon: '❌', err });
  });

  pusherClient = client;
  return client;
}

const channels: Record<string, number> = {};
/** Test hook: forget the binding counts between tests. */
export function resetPusherBindings() {
  for (const channel of Object.keys(channels)) delete channels[channel];
}
export const usePusherSubscription = (
  channel: string,
  event: string,
  onUpdate: (message: PusherMessage) => void
) => {
  // Wrap in callback proxy to avoid resubscribing when callback changes
  // We can just route the message to the new callback instead
  const handleUpdate = useCallbackProxy((...args: ArgumentTypes<typeof onUpdate>) => {
    debug.template.pusher(`${event} recieved`, {
      channel,
      event,
      origin: useTic ? 'TIC' : 'timely',
      payload: args?.[0],
    });
    onUpdate(...args);
  });

  const client = pusherClient;
  useEffect(() => {
    if (!client) {
      debug.template.pusher('Pusher client is not initialized', { icon: '💥' });
      return;
    }
    bindPusherEvent(client, channel, event, handleUpdate);
    return () => {
      unbindPusherEvent(client, channel, event, handleUpdate);
    };
  }, [handleUpdate, client, channel, event]);
};

export function bindPusherEvent(
  pusherClient: Pusher,
  channel: string,
  event: string,
  fn: (message: PusherMessage) => void
) {
  // Always through subscribe(): it returns the channel the client already holds, and it
  // reinstates a subscription that an unsubscribe cancelled while its auth request was still
  // in flight. Looking the channel up instead left exactly that channel known to the client
  // but never subscribed on the server, which is what happens to a channel's only subscriber
  // when React unmounts and remounts it at once (StrictMode, a quick route change).
  const pusherChannel = pusherClient.subscribe(channel);
  pusherChannel.bind(event, fn);

  if (channels[channel] === undefined) channels[channel] = 0;
  channels[channel]++;
}
export function unbindPusherEvent(
  pusherClient: Pusher,
  channel: string,
  event: string,
  fn: (message: PusherMessage) => void
) {
  const pusherChannel = pusherClient.channels.find(channel);
  if (pusherChannel) {
    pusherChannel.unbind(event, fn);
  }

  channels[channel]--;
  if (channels[channel] <= 0) {
    delete channels[channel];
    pusherClient.unsubscribe(channel);
  }
}

/**
 * Typesafe (for TIC Pusher events) version of usePusherSubscription
 * Identical fn signature and actual code (except for type assertion)
 */
export function useTicPusherSubscription<T extends keyof PusherEventPayloadMap>(
  channel: PusherChannelValue,
  event: T,
  onUpdate: (message: PusherEventPayloadMap[T]) => void
) {
  return usePusherSubscription(channel, event, onUpdate as (message: any) => void);
}
